import { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import _ from "lodash";

import t from "lib/i18n";
import useGTM from "hooks/useGTM";
import { trackFavouritesRemoveFavourite } from "lib/tracking";
import { apiState, favouriteTickets, highlightedFavouriteTicket, selectedFavouriteTicket } from "state";
import { deleteFavourite } from "services/FavouritesService";
import Button, { ButtonColors, ButtonSizes, ButtonTypes, ButtonVariants } from 'components/ui/Button';
import Panel from 'components/ui/Panel';
import SrOnly from 'components/helpers/SrOnly';
import { ReactComponent as RemoveFavouriteIcon } from "static/images/favourite-remove.svg";
import customerAxiosInstance from "../../api/customerAxiosInstance";
import { withConfigContext } from "../../hooks/useConfig";
import { withCustomerData } from "../../hooks/useAuth";

const RemoveFavourite = ({ ticket, brand, env, customerData, apiUrl}) => {
  const [tickets, setTickets] = useRecoilState(favouriteTickets);
  const [highlightedTicket, setHighlightedTicket] = useRecoilState(highlightedFavouriteTicket);
  const [selectedTicket, setSelectedTicket] = useRecoilState(selectedFavouriteTicket);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const dispatchGTM = useGTM();

  const onRemoveFavourite = async () => {
    const response = await deleteFavourite(customerAxiosInstance({
      apiUrl: `${apiUrl}/favourite-ticket/customer/${customerData.id}/one-click/${ticket.id}`,
      brand,
      env,
      customerData,
    }));
    if (response) {
      const updatedTickets = tickets.filter(t => t !== ticket);
      const updatedSelectedTicket = updatedTickets.length ? updatedTickets[0] : null;
      setTickets(updatedTickets);
      if (ticket === selectedTicket) {
        setSelectedTicket(updatedSelectedTicket);
      }
      if (ticket === highlightedTicket) {
        setHighlightedTicket(updatedSelectedTicket);
      }
      dispatchGTM(trackFavouritesRemoveFavourite());
    }
  };

  const toggleRemoveFavouritePanel = () => {
    setShowConfirmation(!showConfirmation);
  };

  const panelActions = (
    <>
      <Button
        className="otrl-jp__favourite-ticket__remove-favourite__cancel-button"
        type={ButtonTypes.BUTTON}
        text={t('favourites.ticket.remove.cancel.button')}
        size={ButtonSizes.SM}
        color={ButtonColors.SECONDARY}
        onClick={e => {
          e.stopPropagation();
          toggleRemoveFavouritePanel();
        }}
      />
      <Button
        type={ButtonTypes.SUBMIT}
        size={ButtonSizes.SM}
        autoFocus
        onClick={e => {
          e.stopPropagation();
          onRemoveFavourite();
          toggleRemoveFavouritePanel();
        }}
      >
        <SrOnly>
          {`
            ${t('favourites.ticket.remove.confirm.message')}
            ${t('favourites.ticket.remove.confirm.aria')}
          `}
        </SrOnly>
        <span aria-hidden>{t('favourites.ticket.remove.confirm.button')}</span>
      </Button>
    </>
  );

  return (
    <div className="otrl-jp__favourite-ticket__remove-favourite">
      {showConfirmation ? (
        <div className="otrl-jp__favourite-ticket__remove-favourite__panel">
          <div className="otrl-jp__divider-line" />
          <Panel
            actions={panelActions}
            fullWidth
          >
            {t('favourites.ticket.remove.confirm.message')}
          </Panel>
        </div>
      ) : (
        <Button
          className="otrl-jp__favourite-ticket__remove-favourite__button"
          variant={ButtonVariants.TEXT}
          size={ButtonSizes.SM}
          onClick={e => {
            e.stopPropagation();
            toggleRemoveFavouritePanel();
          }}
        >
          {t('favourites.ticket.remove.button')}
          <RemoveFavouriteIcon className="otrl-jp__favourite-ticket__remove-favourite__icon" />
        </Button>
      )}
    </div>
  );
};

export default _.flow(withCustomerData, withConfigContext)(RemoveFavourite);
